import { Box, Grid, MenuItem } from '@mui/material'
import flatMap from 'lodash/flatMap'
import includes from 'lodash/includes'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import sample from 'lodash/sample'
import uniqBy from 'lodash/uniqBy'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'

import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Title,
    Tooltip,
    TooltipItem,
    ChartData,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** CONSTANTS */
import { colors } from 'constants/theme'

/** COMPONENT */
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import FilterSelect from 'component/Select/FilterSelect'
import { CardSalesProps, handleChangeFilterSelect, renderFilterSelect } from '../dashboard'
import { SelectChangeEvent } from '@mui/material'

/** UTILS || SLICE */
import { getSecureItem, numberFormat } from 'utils/app.utils'

/** API */
import { BranchInterface } from 'api/master/clinic.api'

/** STYLE */
import DashboardApi, { IFBastSellOperativeType, IFSumeryFilter } from 'api/dashboard.api'
import OperativeTypeApi from 'api/setting/treatments/operative-type.api'
import { KEY_STORAGE } from 'constants/common'
import * as UseStyled from '../useStyled'

export default function OperativeType(props: CardSalesProps) {
    const { t } = useTranslation()

    const bg = colors.dashboard.operativeType

    const customBar = {
        maxBarThickness: 40,
        borderRadius: 6,
    }

    // data
    const [labels, setLabels] = useState<string[]>([])
    const [labelProducts, setLabelProducts] = useState<{ data: (string | null)[] }[]>([])
    const [data, setData] = useState<ChartData<'bar'>>({
        labels: [],
        datasets: [
            {
                label: 'a',
                data: [],
                backgroundColor: bg,
                hoverBackgroundColor: bg,
                ...customBar
            },
        ]
    })

    const [loadFirst, setLoadFirst] = useState(false);
    const dashboardMain = getSecureItem(KEY_STORAGE.DASHBOARD)
    const dashboardCurrent = dashboardMain ? JSON.parse(dashboardMain) : {}
    const [optionOperativeType, setOptionOperativeType] = useState<{ itemTypeName: string; itemTypeId: number; color?: string }[]>([])
    const [selectOperativeType, setSelectOperativeType] = useState<(string | number)[]>(dashboardCurrent?.operativeType?.type || ['ALL'])

    const [branches, setBranches] = useState<BranchInterface[]>([])
    const [branch, setBranch] = useState<(string | number)[]>(dashboardCurrent?.operativeType?.branch || ['ALL'])
    const [rangeDate, setRangeDate] = useState(dashboardCurrent?.operativeType?.rangeDate || [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')])

    const handleChangeSelectBranch = (e: SelectChangeEvent<(string | number)[]>) => setBranch(handleChangeFilterSelect(e))

    const handleChangeSelectOperativeType = (e: SelectChangeEvent<(string | number)[]>) => setSelectOperativeType(handleChangeFilterSelect(e))

    const loadData = useCallback(async () => {
        const condition: IFSumeryFilter = { dateStart: rangeDate[0], dateEnd: rangeDate[1], branchId: branch[0] === 'ALL' ? `` : branch.toString(), type: selectOperativeType[0] === 'ALL' ? '' : selectOperativeType.toString() }
        const res = await DashboardApi.findSellOperativeType(condition)

        if (res.status === 200) {
            const summeris: IFBastSellOperativeType[] = res.data;
            const dataLabels: string[] = summeris.map(x => x.branchName);

            const allPayments = flatMap(summeris, 'payments');
            const uniqueOperativeTypes = uniqBy(allPayments, 'operativeTypeId');

            const dataProducts = uniqueOperativeTypes.map(op => {
                const operativeTypeInfo = optionOperativeType.find((opt) => opt.itemTypeId === op.operativeTypeId);
                const color = operativeTypeInfo?.color || op.color || sample(bg);
                return {
                    ...customBar,
                    data: Array(summeris.length).fill(0),
                    backgroundColor: color,
                    operativeTypeId: op.operativeTypeId,
                    operativeTypeName: op.operativeTypeName,
                };
            });

            const dataLabelProducts = uniqueOperativeTypes.map(op => ({
                data: Array(summeris.length).fill(null),
                operativeTypeName: op.operativeTypeName,
            }));

            summeris.forEach((branchData, branchIndex) => {
                branchData.payments.forEach(payment => {
                    const datasetIndex = dataProducts.findIndex(ds => ds.operativeTypeId === payment.operativeTypeId);
                    if (datasetIndex !== -1) {
                        dataProducts[datasetIndex].data[branchIndex] = payment.amountPaid;
                        dataLabelProducts[datasetIndex].data[branchIndex] = payment.operativeTypeName;
                    }
                });
            });

            setLabels(dataLabels);
            setLabelProducts(dataLabelProducts);
            setData({
                labels: dataLabels,
                datasets: dataProducts,
            });
        }
    }, [branch, rangeDate, selectOperativeType, optionOperativeType])

    useEffect(() => {
        loadData()
    }, [loadData])

    useEffect(() => {
        if (!loadFirst) {
            const timer = setTimeout(() => {
                setLoadFirst(true);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [props.rangeDate, props.branches]);

    useEffect(() => {
        setBranches(props.branches || [])
    }, [props.branches])

    useEffect(() => {
        loadFirst && setRangeDate(props.rangeDate || [])
    }, [props.rangeDate])

    useEffect(() => {
        loadFirst && setBranch(props.branch || [])
    }, [props.branch])

    useEffect(() => {
        props?.handleChangeDashboard && props?.handleChangeDashboard("operativeType,branch", branch)
    }, [branch])

    useEffect(() => {
        props?.handleChangeDashboard && props?.handleChangeDashboard("operativeType,rangeDate", rangeDate)
    }, [rangeDate])

    useEffect(() => {
        props?.handleChangeDashboard && props?.handleChangeDashboard("operativeType,type", selectOperativeType)
    }, [selectOperativeType])

    const loadOperativeType = async (search?: string) => {
        const res = await OperativeTypeApi.findAll({ pageLimit: 30, search: search, status: 'ACTIVE', export: 1 })
        if (res.status === 200) {
            const newData = res.data.map((value: { operativeTypeName: string; operativeTypeId: number; color?: string }) => {
                return { itemTypeName: value.operativeTypeName, itemTypeId: value.operativeTypeId, color: value?.color }
            })
            setOptionOperativeType(newData)
        }
    }

    useEffect(() => {
        loadOperativeType()
    }, [])

    const options = {
        responsive: true,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: function (context: TooltipItem<'bar'>) {
                        if (!labelProducts) return numberFormat(context.raw as number)
                        if (!labelProducts[context.datasetIndex].data[context.dataIndex]) return numberFormat(context.raw as number)
                        return ` ${labelProducts[context.datasetIndex].data[context.dataIndex]}: ${numberFormat(context.raw as number)}`
                    },
                }
            },
        },
        scales: {
            x: {
                grid: {
                    display: true,
                    drawBorder: false,
                    drawOnChartArea: false,
                    drawTicks: false,
                    color: colors.lightGray
                },
                ticks: {
                    color: colors.textExtraLightGray,
                    fontSize: 16,
                    padding: 10,
                    maxRotation: 0,
                    minRotation: 0,
                    callback: function (val: string | number, _index: number) {
                        const label = t('DASHBOARD.FILTER.BRANCH') + labels[val as number]
                        const maxLength = 10;


                        const words = label.split(' ');
                        const lines = [];
                        let currentLine = '';

                        for (const word of words) {
                            if (currentLine.length + word.length <= maxLength) {
                                currentLine += word + ' ';
                            } else {
                                lines.push(currentLine.trim());
                                currentLine = word + ' ';
                            }
                        }

                        if (currentLine.trim().length > 0) {
                            lines.push(currentLine.trim());
                        }

                        return lines;
                    },
                }
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: colors.lightGray
                },
                ticks: {
                    color: colors.textExtraLightGray,
                    fontSize: 16,
                    stepSize: 100000,
                    borderRadius: 8,
                    callback: function (val: string | number, _index: number) {
                        return numberFormat(Number(val).toFixed(0))
                    },
                }
            }
        }
    }

    const renderData = useCallback(() => {
        const newOption = selectOperativeType[0] === 'ALL' ? optionOperativeType : optionOperativeType.filter((item) => selectOperativeType.includes(item?.itemTypeId))
        return (
            <Box display={'flex'} alignItems={'center'} flexWrap={'wrap'} gap={'12px'}>
                {newOption.map((item: { itemTypeName: string; itemTypeId: number; color?: string }, index: number) => {
                    return (
                        <Box key={index} display={'flex'} alignItems={'center'} gap={'8px'} flex={'1 1 200px'} maxWidth={'200px'}>
                            <Box minWidth={'20px'} minHeight={'20px'} borderRadius={'4px'} sx={{ backgroundColor: item?.color }} />
                            <Box fontSize={'13px'}>
                                {item?.itemTypeName}
                            </Box>
                        </Box>
                    )
                })}
            </Box>
        )
    }, [data])

    return (
        <UseStyled.Card className='d-flex flex-column'>
            <Box className='pt-3 px-3 px-xl-4' sx={{ borderBottom: `1px solid ${colors.lightGray}`, paddingBottom: '12px' }}>
                <Grid container spacing={1} alignItems={'center'}>
                    <Grid item xs={12} className={'w-100'}>
                        <UseStyled.Title type='title' sx={{ flex: 'none' }}>{t('DASHBOARD.OPERATIVE_TYPE')}</UseStyled.Title>
                    </Grid>
                    <Grid item xs={12} sm={4} className={'w-100'}>
                        <FilterSelect
                            multiple
                            style={{ height: '40px' }}
                            label={t('')}
                            labelId="label-branch"
                            selectId="select-branch"
                            onchange={handleChangeSelectBranch}
                            renderValue={() => `${t('DASHBOARD.FILTER.BRANCH')}: ${renderFilterSelect('branch', branches, branch) === 'ALL' ? t('STATUS.ALL') : renderFilterSelect('branch', branches, branch)}`}
                            value={branch}
                            options={[
                                <MenuItem
                                    key="ALL"
                                    value="ALL"
                                    sx={{
                                        '&.MuiMenuItem-root.Mui-selected': {
                                            pointerEvents: includes(branch, 'ALL') ? `none` : 'auto',
                                            backgroundColor: includes(branch, 'ALL') ? `${colors.themeSecondColor06} !important` : 'transparent',
                                            color: includes(branch, 'ALL') ? `${colors.themeSecondColor} !important` : `${colors.textPrimary}`
                                        }
                                    }}>
                                    {t('STATUS.ALL')}
                                </MenuItem>,
                                map(props.branches, (val: BranchInterface, indexBranch: number) => {
                                    return (
                                        <MenuItem
                                            key={indexBranch}
                                            value={val.branchId}
                                            sx={{
                                                '&.MuiMenuItem-root.Mui-selected': {
                                                    backgroundColor: includes(branch, val.branchId) ? `${colors.themeSecondColor06} !important` : 'transparent',
                                                    color: includes(branch, val.branchId) ? `${colors.themeSecondColor} !important` : `${colors.textPrimary}`
                                                }
                                            }}>
                                            {val.branchName}
                                        </MenuItem>
                                    )
                                })
                            ]}
                            formControlClass={'not-max-width'}
                            classesOption="style-select-doctors"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} className={'w-100'}>
                        <FilterSelect
                            multiple
                            style={{ height: '40px' }}
                            label={t('')}
                            labelId="label-selectOperativeType"
                            selectId="select-selectOperativeType"
                            onchange={handleChangeSelectOperativeType}
                            renderValue={() => `${t('DASHBOARD.OPERATIVE_TYPE')}: ${renderFilterSelect('operativeType', optionOperativeType, selectOperativeType) === 'ALL' ? t('STATUS.ALL') : renderFilterSelect('operativeType', optionOperativeType, selectOperativeType)}`}
                            value={selectOperativeType}
                            options={[
                                <MenuItem
                                    key="ALL"
                                    value="ALL"
                                    sx={{
                                        '&.MuiMenuItem-root.Mui-selected': {
                                            pointerEvents: includes(selectOperativeType, 'ALL') ? `none` : 'auto',
                                            backgroundColor: includes(selectOperativeType, 'ALL') ? `${colors.themeSecondColor06} !important` : 'transparent',
                                            color: includes(selectOperativeType, 'ALL') ? `${colors.themeSecondColor} !important` : `${colors.textPrimary}`
                                        }
                                    }}
                                >
                                    {t('STATUS.ALL')}
                                </MenuItem>,
                                map(optionOperativeType, (val: { itemTypeName: string; itemTypeId: number; color?: string }, index: number) => {
                                    return (
                                        <MenuItem
                                            key={index}
                                            value={val.itemTypeId}
                                            sx={{
                                                '&.MuiMenuItem-root.Mui-selected': {
                                                    backgroundColor: includes(selectOperativeType, val.itemTypeId) ? `${colors.themeSecondColor06} !important` : 'transparent',
                                                    color: includes(selectOperativeType, val.itemTypeId) ? `${colors.themeSecondColor} !important` : `${colors.textPrimary}`
                                                }
                                            }}
                                        >
                                            {val.itemTypeName}
                                        </MenuItem>
                                    )
                                })
                            ]}
                            formControlClass={'not-max-width'}
                            classesOption="style-select-doctors"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} className={'w-100'}>
                        <InputSecondNewRangePicker
                            inputHeight={40}
                            value={rangeDate}
                            onchange={(val) => {
                                if (isEmpty(val)) setRangeDate(emptyRangePicker)
                                else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
                            }}
                            allowClear
                            onClear={() => setRangeDate(emptyRangePicker)}
                        />
                    </Grid>
                </Grid>
            </Box>
            <Box className='p-3 px-xl-4 h-100'>
                <Bar options={options} data={data} />
                <Box>
                    {renderData()}
                </Box>
            </Box>
        </UseStyled.Card>
    )
}

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
)